import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Check,
  CircleDot,
  Clock,
  Copy,
  GitBranch,
  GitMerge,
  GitPullRequest,
  GitPullRequestClosed,
  X,
} from 'lucide-react'
import type { ComponentType } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible'
import { CollapsibleSectionHeader } from '@/components/app/layout/collapsible-section-header'
import { usePullRequests } from '@/lib/db/rxdb/hooks'
import {
  DEFAULT_BRANCH_FORMAT,
  renderBranchName,
  useScmBinding,
  useScmIdentities,
} from '@/lib/api/features/scm'
import { cn } from '@/lib/utils'

interface LinkedPullRequestsSectionProps {
  taskId: string
  taskName?: string
  taskIdentifier?: string | null
  /** The task's project — decides whether the branch hint is relevant at all. */
  projectId?: string | null
  className?: string
}

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline'

const STATE_ICON: Record<string, ComponentType<{ className?: string }>> = {
  open: GitPullRequest,
  draft: GitPullRequest,
  merged: GitMerge,
  closed: GitPullRequestClosed,
}

const STATE_VARIANT: Record<string, BadgeVariant> = {
  open: 'default',
  draft: 'outline',
  merged: 'secondary',
  closed: 'outline',
}

const CI_ICON: Record<string, ComponentType<{ className?: string }>> = {
  passing: Check,
  failing: X,
  pending: Clock,
}

const CI_VARIANT: Record<string, BadgeVariant> = {
  passing: 'default',
  failing: 'destructive',
  pending: 'secondary',
}

const REVIEW_ICON: Record<string, ComponentType<{ className?: string }>> = {
  approved: Check,
  changes_requested: X,
  review_required: CircleDot,
}

const REVIEW_VARIANT: Record<string, BadgeVariant> = {
  approved: 'default',
  changes_requested: 'destructive',
  review_required: 'secondary',
}

/**
 * Read-only list of pull requests linked to a task. Populated server-side (Super Agent relay +
 * GitHub webhook); a task can have multiple PRs over its life, and merged/closed ones stay as
 * durable history. No mutations here — the client never writes this collection.
 */
export function LinkedPullRequestsSection({
  taskId,
  taskName,
  taskIdentifier,
  projectId,
  className,
}: LinkedPullRequestsSectionProps) {
  const { t } = useTranslation('tasks')
  const { pullRequests, isLoading } = usePullRequests(taskId)
  const { binding, isLoading: isBindingLoading } = useScmBinding(projectId)
  const identities = useScmIdentities()
  const [isOpen, setIsOpen] = useState(true)

  if (isLoading) return null

  // Before any PR exists, offer the convention branch name to copy (auto-links on push), but only
  // where a branch could actually be pushed — i.e. the task's project has a repo bound. Otherwise
  // every freshly created task would show a git hint that leads nowhere (MOL-23). Uses the
  // binding's configured format so the copied name matches what the project agreed on.
  if (pullRequests.length === 0) {
    if (isBindingLoading || !binding) return null
    const branch = renderBranchName(
      { taskId, taskName: taskName ?? '', identifier: taskIdentifier },
      binding.branchFormat ?? DEFAULT_BRANCH_FORMAT,
    )
    return (
      <div className={cn('flex items-center gap-2 px-2 py-1 text-xs text-muted-foreground', className)}>
        <GitBranch className="size-3.5 shrink-0" />
        <code className="flex-1 truncate font-mono text-[11px]">{branch}</code>
        <Button
          variant="ghost"
          size="icon"
          className="size-6"
          title={t('pullRequests.copyBranch')}
          onClick={() => {
            void navigator.clipboard.writeText(branch)
            toast.success(t('pullRequests.copied'))
          }}
        >
          <Copy className="size-3" />
        </Button>
      </div>
    )
  }

  // Most relevant first: open > draft > merged/closed, then newest provider update.
  const order: Record<string, number> = { open: 0, draft: 1, merged: 2, closed: 3 }
  const sorted = [...pullRequests].sort(
    (a, b) =>
      (order[a.state] ?? 9) - (order[b.state] ?? 9) ||
      b.providerUpdatedAt - a.providerUpdatedAt,
  )

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={className}>
      <CollapsibleSectionHeader
        icon={<GitPullRequest className="size-4" />}
        title={t('pullRequests.title')}
        count={sorted.length}
        isOpen={isOpen}
      />
      <CollapsibleContent className="pt-1 space-y-1">
        {sorted.map((pr) => {
          const StateIcon = STATE_ICON[pr.state] ?? GitPullRequest
          const CiIcon = CI_ICON[pr.ciStatus]
          const ReviewIcon = REVIEW_ICON[pr.reviewStatus]
          const href = pr.htmlUrl ?? pr.url ?? undefined
          return (
            <a
              key={pr.id}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'group flex items-center gap-2 px-2 py-1 rounded-md hover:bg-accent transition-colors',
                !href && 'pointer-events-none',
              )}
            >
              <StateIcon className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="flex-1 truncate text-sm">
                <span className="text-muted-foreground">#{pr.number}</span>{' '}
                {pr.sourceBranch ?? pr.repositoryFullName}
                {pr.authorGitHubLogin && (
                  <span className="ml-1.5 text-[11px] text-muted-foreground">
                    {identities.get(pr.authorGitHubLogin.toLowerCase())?.name ?? `@${pr.authorGitHubLogin}`}
                  </span>
                )}
              </span>
              <Badge variant={STATE_VARIANT[pr.state] ?? 'outline'} className="shrink-0">
                {t(`pullRequests.state.${pr.state}`, pr.state)}
              </Badge>
              {pr.ciStatus !== 'unknown' && (
                <Badge variant={CI_VARIANT[pr.ciStatus] ?? 'outline'} className="shrink-0">
                  {CiIcon ? <CiIcon /> : null}
                  {t(`pullRequests.ci.${pr.ciStatus}`, pr.ciStatus)}
                </Badge>
              )}
              {pr.reviewStatus !== 'none' && (
                <Badge variant={REVIEW_VARIANT[pr.reviewStatus] ?? 'outline'} className="shrink-0">
                  {ReviewIcon ? <ReviewIcon /> : null}
                  {t(`pullRequests.review.${pr.reviewStatus}`, pr.reviewStatus)}
                </Badge>
              )}
            </a>
          )
        })}
      </CollapsibleContent>
    </Collapsible>
  )
}
